/**
 * Parents management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { ParentsDataTable } from '@/components/parents/ParentsDataTable';
import { CreateParentView } from '@/components/parents/CreateParentView';
import { EditParentView } from '@/components/parents/EditParentView';
import { CreateStudentView } from '@/components/students/remote/CreateStudentView';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { useAuthStore } from '@/stores/authStore';
import { onboardingKeys, useGetParentRemoteFullState } from '@/hooks/useUsers';
import { exportExcel } from '@/services/parents.service';
import { Button } from '@/components/forms/Button';
import * as Icons from 'lucide-react';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  type ParentTabView,
  PARENT_TAB_LABELS,
  PARENT_TAB_COLORS,
  PARENT_TAB_ICONS,
  getParentBusinessStatus,
} from '@/components/parents/parent-configs';
import type { ParentRecord, OnboardingFilters } from '@/types/parent.types';

/** Which form is mounted in the collapse wrapper (parent create/edit or invite-child). */
type ActiveForm = 'create' | 'edit' | 'create-child' | null;

/** Remote parents list with tab filters; can also open a child-student create form. */
export const ParentsManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canExportExcel, canArchiveRow, canReactivateRow } =
    usePageActions('parents');
  const { canCreate: canCreateStudent } = usePageActions('students');
  const { canViewInterface } = usePermissions();
  const canViewStudents = canViewInterface('students');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const { elements: sidebarElements } = useInterfaceElements('sidebar');

  const navigationType = useNavigationType();
  /** Restore the last tab on browser back (`POP`); otherwise default to `parents`. */
  const [activeTab, setActiveTab] = React.useState<ParentTabView>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:parents') as ParentTabView | null;
      if (saved) return saved;
    }
    return 'parents';
  });
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:parents', activeTab);
  }, [activeTab]);
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [editStepKey, setEditStepKey] = React.useState<string | undefined>(undefined);
  const [inviteParentRecord, setInviteParentRecord] = React.useState<ParentRecord | null>(null);

  const { viewMode, setViewMode } = useViewMode('parents-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();

  /** Deep-link: expand a parent row via location.state (from the students page). */
  const [openParentUserId, setOpenParentUserId] = React.useState<string | undefined>(() => {
    const state = location.state as { openParentUserId?: string } | null;
    return state?.openParentUserId;
  });

  React.useEffect(() => {
    const state = location.state as { openParentUserId?: string } | null;
    if (state?.openParentUserId) {
      setOpenParentUserId(state.openParentUserId);
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const { data: deepLinkFullState } = useGetParentRemoteFullState(
    openParentUserId ?? '',
    companyId,
    { enabled: !!openParentUserId && !!companyId },
  );

  React.useEffect(() => {
    if (openParentUserId) setOpenRow(openParentUserId);
  }, [openParentUserId, setOpenRow]);

  React.useEffect(() => {
    const record = deepLinkFullState?.record;
    if (!record || !openParentUserId) return;
    setActiveTab(getParentBusinessStatus(record).tab);
  }, [deepLinkFullState, openParentUserId]);

  /** Download the current filtered list as xlsx (blob URL is revoked after click). */
  const handleExportExcel = async (
    filters: Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>,
  ) => {
    try {
      const blob = await exportExcel(filters, companyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parents-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  /** Close the form and drop edit / invite-child state so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    setInviteParentRecord(null);
  }, []);

  /** Close the form and refresh onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    setInviteParentRecord(null);
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  }, [queryClient]);

  /** Open the remote-student create form pre-filled with this parent. */
  const handleInviteChild = (_id: string, rec: ParentRecord) => {
    closeRow();
    setInviteParentRecord(rec);
    setActiveForm('create-child');
  };

  /** Jump to students filtered by this parent's user id. */
  const handleViewStudents = (_id: string, rec: ParentRecord) => {
    void navigate('/students', {
      state: {
        linkedUserId: rec.userId,
        personName: `${rec.user?.firstName ?? ''} ${rec.user?.lastName ?? ''}`.trim(),
      },
    });
  };

  /** Stable JSX node for the form — avoids unmounting {@link EditParentView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateParentView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editUserId) {
      return (
        <EditParentView
          key={editUserId}
          userId={editUserId}
          stepKey={editStepKey}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    if (activeForm === 'create-child' && inviteParentRecord) {
      return (
        <CreateStudentView
          onSuccess={handleFormSuccess}
          onClose={closeForm}
          initialParentUserId={inviteParentRecord.userId}
          initialParentName={`${inviteParentRecord.user?.firstName ?? ''} ${inviteParentRecord.user?.lastName ?? ''}`.trim()}
          initialParentEmail={inviteParentRecord.user?.email}
          initialParentPhone={inviteParentRecord.user?.phone ?? undefined}
        />
      );
    }
    return null;
  }, [activeForm, editUserId, editStepKey, inviteParentRecord, handleFormSuccess, closeForm]);

  const parentTabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-parents');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return (Object.keys(PARENT_TAB_LABELS) as ParentTabView[]).map((id) => {
      const Icon = id === 'parents' ? MainIcon : PARENT_TAB_ICONS[id];
      return {
        id,
        label: PARENT_TAB_LABELS[id],
        icon: <Icon className="w-3 h-3" />,
        color: PARENT_TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  return (
    <ManagementPage
      title="Gestion des Parents"
      subtitle="Créez, modifiez et gérez tous vos parents"
      tabs={{
        items: parentTabItems,
        activeTab,
        onTabChange: (id) => {
          closeForm();
          closeRow();
          setActiveTab(id as ParentTabView);
        },
      }}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              closeRow();
              setActiveForm('create');
            }}
          >
            Créer un parent
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <ParentsDataTable
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          expandedRowId={openRowId}
          onToggleRow={(id) => {
            openRow(id);
            setOpenParentUserId(undefined);
          }}
          onCloseDetails={closeRow}
          onEditParent={(id) => {
            setEditUserId(id);
            setEditStepKey('basic_information');
            setActiveForm('edit');
            closeRow();
          }}
          enableFilters={true}
          activeTab={activeTab}
          onInviteChild={handleInviteChild}
          onViewStudents={handleViewStudents}
          pageActions={{
            canEditRow,
            canViewRow: true,
            canInviteChild: canCreateStudent,
            canViewStudents,
            canArchiveRow,
            canReactivateRow,
          }}
          showExport={canExportExcel}
          onExportExcel={handleExportExcel}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
